import { Trans, useLingui } from '@lingui/react/macro';
import { Info, PoundSterling, TrendingUp, Users, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DayLabel } from './day-label';
import { DAY_COLUMNS } from '@/domain/templates/day-of-week';
import {
  defaultDemandScaling,
  toDemandValue,
  withDayValue,
} from '@/domain/templates/demand-scaling';
import { cn } from '@/lib/utils';
import type { DemandScaling } from '@/types/template';

interface ScalingBaselineProps {
  /** Current settings, or undefined when the template has none yet. */
  value: DemandScaling | undefined;
  /** Emits the full settings object on every change. */
  onChange: (scaling: DemandScaling) => void;
}

/**
 * The template-level "Scale to demand" editor (in the basics modal): an on/off
 * switch, a single demand metric (Guests / Sales), and a baseline given either
 * as a weekly total or seven per-day figures. Drives the per-shift scaling
 * controls and the preview's forecast playground.
 */
export function ScalingBaseline({ value, onChange }: ScalingBaselineProps) {
  const { t } = useLingui();
  const scaling = value ?? defaultDemandScaling();
  const isSales = scaling.metric === 'sales';
  const update = (patch: Partial<DemandScaling>) => onChange({ ...scaling, ...patch });

  return (
    <div
      className={
        'space-y-3 rotaready:mb-5 rotaready:grid ' +
        'rotaready:grid-cols-[200px_1fr] rotaready:gap-x-5 ' +
        'rotaready:space-y-0'
      }
    >
      <div className="flex items-start justify-between gap-3 rotaready:contents">
        <div className="flex items-start gap-2.5 rotaready:flex-col rotaready:justify-center rotaready:items-end rotaready:gap-1">
          <div
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-lg rotaready:hidden',
              scaling.enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
            )}
          >
            <TrendingUp className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold rotaready:justify-end rotaready:text-base">
              <Trans>Scale to demand</Trans>
            </div>
            <p className="mt-0.5 max-w-sm text-xs text-muted-foreground rotaready:text-right rotaready:text-sm">
              <Trans>
                Auto-adjust headcount based on forecast sales or guests when a rota is generated.
              </Trans>
            </p>
          </div>
        </div>
        <Switch
          checked={scaling.enabled}
          onCheckedChange={(enabled) => update({ enabled })}
          aria-label={t`Scale to demand`}
          className="rotaready:self-center"
        />
      </div>

      {scaling.enabled && (
        <div className="space-y-4 rounded-lg border bg-muted/30 p-3.5 rotaready:col-start-2">
          <div className="space-y-1.5">
            <div className="text-xs font-semibold rotaready:text-base">
              <Trans>What drives demand?</Trans>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <MetricCard
                active={scaling.metric === 'guests'}
                icon={Users}
                title={t`Guests`}
                hint={t`Covers / headcount`}
                onSelect={() => update({ metric: 'guests' })}
              />
              <MetricCard
                active={isSales}
                icon={PoundSterling}
                title={t`Sales`}
                hint={t`Revenue (£)`}
                onSelect={() => update({ metric: 'sales' })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold rotaready:text-base">
              {isSales ? (
                <Trans>Typical sales — the baseline this template is built for</Trans>
              ) : (
                <Trans>Typical guests — the baseline this template is built for</Trans>
              )}
            </div>
            <div className="flex gap-1.5">
              <Button
                type="button"
                size="sm"
                variant={scaling.period === 'weekly' ? 'default' : 'outline'}
                onClick={() => update({ period: 'weekly' })}
              >
                <Trans>Weekly total</Trans>
              </Button>
              <Button
                type="button"
                size="sm"
                variant={scaling.period === 'daily' ? 'default' : 'outline'}
                onClick={() => update({ period: 'daily' })}
              >
                <Trans>By day</Trans>
              </Button>
            </div>

            {scaling.period === 'weekly' ? (
              <div className="flex items-center gap-2">
                {isSales && <span className="text-sm text-muted-foreground">£</span>}
                <Input
                  type="number"
                  min={0}
                  className="w-36 rotaready:h-10 rotaready:text-base"
                  value={scaling.weekly}
                  onChange={(event) => update({ weekly: toDemandValue(event.target.value) })}
                  aria-label={isSales ? t`Weekly sales baseline` : t`Weekly guests baseline`}
                />
                <span className="text-xs text-muted-foreground rotaready:text-sm">
                  {isSales ? <Trans>per week</Trans> : <Trans>guests per week</Trans>}
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1.5">
                {DAY_COLUMNS.map((column, index) => (
                  <div key={column.key} className="space-y-1">
                    <div className="text-center text-[length:var(--extra-small-font-size)] leading-[var(--extra-small-line-height)] font-medium text-muted-foreground">
                      <DayLabel columnKey={column.key} />
                    </div>
                    <Input
                      id={`baseline-day-${column.key}`}
                      type="number"
                      min={0}
                      className="px-1 text-center rotaready:h-10"
                      value={scaling.daily[index] ?? 0}
                      onChange={(event) =>
                        update({
                          daily: withDayValue(
                            scaling.daily,
                            index,
                            toDemandValue(event.target.value),
                          ),
                        })
                      }
                      aria-label={`${column.key} baseline`}
                    />
                  </div>
                ))}
              </div>
            )}

            <p className="flex items-center gap-1.5 text-[length:var(--extra-small-font-size)] leading-[var(--extra-small-line-height)] text-muted-foreground rotaready:text-sm">
              <Info className="size-3 shrink-0" />
              <Trans>You'll set how each shift responds to this in the shift form.</Trans>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface MetricCardProps {
  active: boolean;
  icon: LucideIcon;
  title: string;
  hint: string;
  onSelect: () => void;
}

/** One of the two demand-metric choice cards. */
function MetricCard({ active, icon: Icon, title, hint, onSelect }: MetricCardProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors',
        active ? 'border-primary bg-primary/5' : 'hover:bg-accent',
      )}
    >
      <Icon className={cn('size-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
      <div>
        <div className={cn('text-sm font-semibold rotaready:text-base', active && 'text-primary')}>
          {title}
        </div>
        <div className="text-[length:var(--extra-small-font-size)] leading-[var(--extra-small-line-height)] text-muted-foreground rotaready:text-sm">
          {hint}
        </div>
      </div>
    </button>
  );
}
